import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@material-ui/icons";
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
} from "@material-ui/core";

import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { allowedModels, getModelLabel } from "../../constants/aiModels";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
  },
  helper: {
    display: "block",
    marginBottom: theme.spacing(1),
  },
  groupCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  conditionCard: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  conditionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
  },
}));

const createEmptyCondition = (type = "contactTag") => {
  if (type === "kanbanTag") {
    return { type: "kanbanTag", op: "any", tagIds: [], tagNames: [] };
  }
  if (type === "messageContains") {
    return {
      type: "messageContains",
      source: "contact",
      lastN: 3,
      op: "any",
      keywords: [],
      regex: "",
      caseSensitive: false,
    };
  }
  if (type === "lastMessageAge") {
    return {
      type: "lastMessageAge",
      source: "attendant",
      op: "olderThan",
      minutes: 5,
    };
  }
  return { type: "contactTag", op: "all", tagIds: [], tagNames: [] };
};

const createEmptyGroup = () => ({
  id: `g${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  match: "all",
  conditions: [createEmptyCondition("contactTag")],
});

const TICKET_STATUS_OPTIONS = [
  { value: "pending", label: "Pendente" },
  { value: "open", label: "Aberto (em atendimento)" },
  { value: "lgpd", label: "LGPD" },
  { value: "group", label: "Grupo" },
  { value: "nps", label: "NPS" },
  { value: "closed", label: "Fechado" },
];

const defaultRules = {
  onNewTicket: { enabled: false },
  onInactivity: {
    enabled: false,
    minutes: 30,
    whenLastMessageFromMe: true,
  },
  ticketFilters: {
    statuses: ["pending"],
    allowWithUser: false,
  },
  conditions: {
    enabled: false,
    groupsMatch: "any",
    groups: [],
  },
  messageSignature: {
    enabled: false,
    name: "",
  },
  ai: {
    enabled: false,
    apiKey: "",
    model: "gpt-3.5-turbo-1106",
    maxMessages: 10,
    prompt: "",
    allowStage: true,
    allowClose: false,
    allowQueue: false,
    allowTag: false,
  },
};

const normalizeTicketFilters = (raw) => {
  const defaults = defaultRules.ticketFilters;
  if (!raw || typeof raw !== "object") {
    return { ...defaults };
  }
  const allowed = new Set(TICKET_STATUS_OPTIONS.map((o) => o.value));
  const statuses = Array.isArray(raw.statuses)
    ? [
        ...new Set(
          raw.statuses
            .map((s) => String(s || "").trim().toLowerCase())
            .filter((s) => allowed.has(s))
        ),
      ]
    : [];
  return {
    statuses: statuses.length ? statuses : [...defaults.statuses],
    allowWithUser: !!raw.allowWithUser,
  };
};

const normalizeConditions = (raw) => {
  if (!raw || typeof raw !== "object") {
    return { ...defaultRules.conditions };
  }

  if (Array.isArray(raw.groups)) {
    return {
      enabled: !!raw.enabled,
      groupsMatch: raw.groupsMatch === "all" ? "all" : "any",
      groups: raw.groups.map((group, idx) => ({
        id: group.id || `g${idx + 1}`,
        match: group.match === "any" ? "any" : "all",
        conditions: Array.isArray(group.conditions)
          ? group.conditions.map((c) => {
              if (c?.type === "kanbanTag") {
                return {
                  type: "kanbanTag",
                  op: c.op === "all" ? "all" : "any",
                  tagIds: Array.isArray(c.tagIds) ? c.tagIds : [],
                  tagNames: Array.isArray(c.tagNames) ? c.tagNames : [],
                };
              }
              if (c?.type === "messageContains") {
                return {
                  type: "messageContains",
                  source: c.source === "any" || c.source === "agent" ? c.source : "contact",
                  lastN: Number(c.lastN) > 0 ? Number(c.lastN) : 3,
                  op: c.op === "all" ? "all" : "any",
                  keywords: Array.isArray(c.keywords) ? c.keywords : [],
                  regex: c.regex || "",
                  caseSensitive: !!c.caseSensitive,
                };
              }
              if (c?.type === "lastMessageAge") {
                const allowedSources = new Set([
                  "contact",
                  "attendant",
                  "agent",
                  "other",
                ]);
                return {
                  type: "lastMessageAge",
                  source: allowedSources.has(c.source) ? c.source : "attendant",
                  op: c.op === "newerThan" ? "newerThan" : "olderThan",
                  minutes: Math.max(1, Number(c.minutes) || 5),
                };
              }
              return {
                type: "contactTag",
                op: c?.op === "any" ? "any" : "all",
                tagIds: Array.isArray(c?.tagIds) ? c.tagIds : [],
                tagNames: Array.isArray(c?.tagNames) ? c.tagNames : [],
              };
            })
          : [],
      })),
    };
  }

  // Legacy: contactTagIds / kanbanTagIds / match / requireLandingMessage
  const match = raw.match === "all" ? "all" : "any";
  const legacyConditions = [];

  if (Array.isArray(raw.contactTagIds) && raw.contactTagIds.length > 0) {
    legacyConditions.push({
      type: "contactTag",
      op: match,
      tagIds: raw.contactTagIds,
      tagNames: Array.isArray(raw.contactTagNames) ? raw.contactTagNames : [],
    });
  }
  if (Array.isArray(raw.kanbanTagIds) && raw.kanbanTagIds.length > 0) {
    legacyConditions.push({
      type: "kanbanTag",
      op: match,
      tagIds: raw.kanbanTagIds,
      tagNames: Array.isArray(raw.kanbanTagNames) ? raw.kanbanTagNames : [],
    });
  }
  if (raw.requireLandingMessage) {
    legacyConditions.push({
      type: "messageContains",
      source: "contact",
      lastN: 1,
      op: "any",
      keywords: [],
      regex: "",
      caseSensitive: false,
    });
  }

  return {
    enabled: !!raw.enabled,
    groupsMatch: match === "all" ? "all" : "any",
    groups:
      legacyConditions.length > 0
        ? [{ id: "g1", match, conditions: legacyConditions }]
        : [],
  };
};

const isValidCondition = (condition) => {
  if (!condition) return false;
  if (condition.type === "contactTag" || condition.type === "kanbanTag") {
    return Array.isArray(condition.tagIds) && condition.tagIds.length > 0;
  }
  if (condition.type === "messageContains") {
    const hasKeywords =
      Array.isArray(condition.keywords) &&
      condition.keywords.some((k) => String(k || "").trim());
    const hasRegex = !!String(condition.regex || "").trim();
    return hasKeywords || hasRegex;
  }
  if (condition.type === "lastMessageAge") {
    return (
      ["contact", "attendant", "agent", "other"].includes(condition.source) &&
      ["olderThan", "newerThan"].includes(condition.op) &&
      Number(condition.minutes) >= 1
    );
  }
  return false;
};

const FlowBuilderTriggerRulesModal = ({ open, onClose, flowId, flowName }) => {
  const classes = useStyles();

  const [rules, setRules] = useState(defaultRules);
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contactTagOptions, setContactTagOptions] = useState([]);
  const [kanbanOptions, setKanbanOptions] = useState([]);

  useEffect(() => {
    if (!open || !flowId) return;

    const fetchRules = async () => {
      try {
        const { data } = await api.get(`/flowbuilder/triggers/${flowId}`);
        const loaded =
          typeof data?.triggerRules === "string"
            ? JSON.parse(data.triggerRules)
            : data?.triggerRules;
        if (loaded) {
          setRules({
            ...defaultRules,
            ...loaded,
            onNewTicket: {
              ...defaultRules.onNewTicket,
              ...(loaded.onNewTicket || {}),
            },
            onInactivity: {
              ...defaultRules.onInactivity,
              ...(loaded.onInactivity || {}),
            },
            ticketFilters: normalizeTicketFilters(loaded.ticketFilters),
            conditions: normalizeConditions(loaded.conditions),
            messageSignature: {
              ...defaultRules.messageSignature,
              ...(loaded.messageSignature || {}),
            },
            ai: { ...defaultRules.ai, ...(loaded.ai || {}) },
          });
        } else {
          setRules(defaultRules);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchRules();
  }, [open, flowId]);

  useEffect(() => {
    if (!open) return;

    api
      .get(`/tags/list`, { params: { kanban: 0 } })
      .then(({ data }) => {
        const tags = Array.isArray(data) ? data : [];
        setContactTagOptions(
          tags.map((tag) => ({ id: tag.id, name: tag.name }))
        );
      })
      .catch(() => setContactTagOptions([]));

    api
      .get(`/tag/kanban`)
      .then(({ data }) => {
        const lanes = Array.isArray(data?.lista)
          ? data.lista
          : Array.isArray(data)
            ? data
            : [];
        setKanbanOptions(lanes.map((tag) => ({ id: tag.id, name: tag.name })));
      })
      .catch(() => setKanbanOptions([]));
  }, [open]);

  const handleClose = () => {
    setShowApiKey(false);
    setRules(defaultRules);
    onClose();
  };

  const setGroup = (group, key, value) =>
    setRules((prev) => ({
      ...prev,
      [group]: { ...prev[group], [key]: value },
    }));

  const updateConditions = (updater) =>
    setRules((prev) => ({
      ...prev,
      conditions: updater(prev.conditions),
    }));

  const resolveTagNames = (tagIds, options) =>
    options
      .filter((t) => tagIds.includes(t.id))
      .map((t) => t.name);

  const handleSave = async () => {
    if (rules.conditions.enabled) {
      const groups = rules.conditions.groups || [];
      const hasValidGroup = groups.some(
        (g) =>
          Array.isArray(g.conditions) &&
          g.conditions.some((c) => isValidCondition(c))
      );
      if (!hasValidGroup) {
        toast.error(
          "Com condições habilitadas, adicione ao menos um cenário com uma condição válida (tags ou palavras/regex)."
        );
        return;
      }
    }

    const conditionsToSave = {
      enabled: !!rules.conditions.enabled,
      groupsMatch: rules.conditions.groupsMatch || "any",
      groups: (rules.conditions.groups || []).map((group) => ({
        ...group,
        conditions: (group.conditions || []).map((c) => {
          if (c.type === "contactTag") {
            return {
              ...c,
              tagNames: resolveTagNames(c.tagIds || [], contactTagOptions),
            };
          }
          if (c.type === "kanbanTag") {
            return {
              ...c,
              tagNames: resolveTagNames(c.tagIds || [], kanbanOptions),
            };
          }
          if (c.type === "messageContains") {
            return {
              ...c,
              keywords: (c.keywords || [])
                .map((k) => String(k).trim())
                .filter(Boolean),
            };
          }
          if (c.type === "lastMessageAge") {
            return {
              type: "lastMessageAge",
              source: c.source || "attendant",
              op: c.op === "newerThan" ? "newerThan" : "olderThan",
              minutes: Math.max(1, Number(c.minutes) || 5),
            };
          }
          return c;
        }),
      })),
    };

    if (rules.messageSignature?.enabled && !String(rules.messageSignature?.name || "").trim()) {
      toast.error("Informe o nome da assinatura ou desabilite a opção.");
      return;
    }

    const ticketFiltersToSave = normalizeTicketFilters(rules.ticketFilters);
    if (!ticketFiltersToSave.statuses.length) {
      toast.error("Selecione ao menos um status em que o fluxo pode ser acionado.");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/flowbuilder/triggers`, {
        flowId,
        triggerRules: {
          ...rules,
          conditions: conditionsToSave,
          ticketFilters: ticketFiltersToSave,
          messageSignature: {
            enabled: !!rules.messageSignature?.enabled,
            name: String(rules.messageSignature?.name || "").trim(),
          },
        },
      });
      toast.success("Configurações do fluxo salvas com sucesso");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const renderTagMultiSelect = ({
    label,
    labelId,
    value,
    options,
    onChange,
  }) => (
    <FormControl fullWidth margin="dense" variant="outlined">
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        multiple
        labelId={labelId}
        label={label}
        value={value || []}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) =>
          options
            .filter((t) => selected.includes(t.id))
            .map((t) => t.name)
            .join(", ")
        }
      >
        {options.map((tag) => (
          <MenuItem key={tag.id} value={tag.id}>
            <Checkbox
              color="primary"
              checked={(value || []).includes(tag.id)}
            />
            <ListItemText primary={tag.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderConditionFields = (groupIndex, conditionIndex, condition) => {
    const patchCondition = (patch) =>
      updateConditions((conds) => {
        const groups = [...(conds.groups || [])];
        const group = { ...groups[groupIndex] };
        const conditions = [...(group.conditions || [])];
        conditions[conditionIndex] = { ...conditions[conditionIndex], ...patch };
        group.conditions = conditions;
        groups[groupIndex] = group;
        return { ...conds, groups };
      });

    if (condition.type === "contactTag" || condition.type === "kanbanTag") {
      const options =
        condition.type === "contactTag" ? contactTagOptions : kanbanOptions;
      return (
        <>
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Operador</InputLabel>
            <Select
              label="Operador"
              value={condition.op || "any"}
              onChange={(e) => patchCondition({ op: e.target.value })}
            >
              <MenuItem value="any">Qualquer (OU)</MenuItem>
              <MenuItem value="all">Todas (E)</MenuItem>
            </Select>
          </FormControl>
          {renderTagMultiSelect({
            label:
              condition.type === "contactTag"
                ? "Tags do contato"
                : "Etapas do Kanban",
            labelId: `cond-${groupIndex}-${conditionIndex}-tags`,
            value: condition.tagIds || [],
            options,
            onChange: (tagIds) => patchCondition({ tagIds }),
          })}
        </>
      );
    }

    if (condition.type === "lastMessageAge") {
      return (
        <>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Origem</InputLabel>
                <Select
                  label="Origem"
                  value={condition.source || "attendant"}
                  onChange={(e) => patchCondition({ source: e.target.value })}
                >
                  <MenuItem value="contact">Contato</MenuItem>
                  <MenuItem value="attendant">Atendente</MenuItem>
                  <MenuItem value="agent">Agente</MenuItem>
                  <MenuItem value="other">Outros</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Operador</InputLabel>
                <Select
                  label="Operador"
                  value={condition.op || "olderThan"}
                  onChange={(e) => patchCondition({ op: e.target.value })}
                >
                  <MenuItem value="olderThan">Há mais de</MenuItem>
                  <MenuItem value="newerThan">Há menos de</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TextField
            label="Minutos"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            value={condition.minutes ?? 5}
            onChange={(e) =>
              patchCondition({ minutes: Math.max(1, Number(e.target.value) || 1) })
            }
            inputProps={{ min: 1 }}
            helperText="Se não houver mensagem dessa origem: 'há mais de' passa; 'há menos de' falha."
          />
        </>
      );
    }

    return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <FormControl fullWidth margin="dense" variant="outlined">
              <InputLabel>Origem</InputLabel>
              <Select
                label="Origem"
                value={condition.source || "contact"}
                onChange={(e) => patchCondition({ source: e.target.value })}
              >
                <MenuItem value="contact">Contato</MenuItem>
                <MenuItem value="agent">Agente</MenuItem>
                <MenuItem value="any">Qualquer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Últimas N msgs"
              type="number"
              variant="outlined"
              margin="dense"
              fullWidth
              value={condition.lastN ?? 3}
              onChange={(e) =>
                patchCondition({ lastN: Number(e.target.value) || 1 })
              }
              inputProps={{ min: 1, max: 50 }}
            />
          </Grid>
        </Grid>
        <FormControl fullWidth margin="dense" variant="outlined">
          <InputLabel>Operador keywords</InputLabel>
          <Select
            label="Operador keywords"
            value={condition.op || "any"}
            onChange={(e) => patchCondition({ op: e.target.value })}
          >
            <MenuItem value="any">Qualquer palavra (OU)</MenuItem>
            <MenuItem value="all">Todas as palavras (E)</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Palavras-chave (uma por linha)"
          variant="outlined"
          margin="dense"
          fullWidth
          multiline
          rows={3}
          value={(condition.keywords || []).join("\n")}
          onChange={(e) =>
            patchCondition({
              keywords: e.target.value.split("\n"),
            })
          }
          helperText="Cada linha é uma palavra/frase"
        />
        <TextField
          label="Regex (opcional)"
          variant="outlined"
          margin="dense"
          fullWidth
          value={condition.regex || ""}
          onChange={(e) => patchCondition({ regex: e.target.value })}
        />
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={!!condition.caseSensitive}
              onChange={(e) =>
                patchCondition({ caseSensitive: e.target.checked })
              }
            />
          }
          label="Diferenciar maiúsculas/minúsculas"
        />
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
        <DialogTitle>
          Configurações do fluxo{flowName ? ` — ${flowName}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="textSecondary" className={classes.helper}>
            Defina quando este fluxo será chamado automaticamente e como as mensagens
            são enviadas. Os acionamentos são opcionais e complementares ao disparo por
            frase (phrase-list).
          </Typography>

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!rules.onNewTicket.enabled}
                  onChange={(e) =>
                    setGroup("onNewTicket", "enabled", e.target.checked)
                  }
                />
              }
              label="Ao criar um novo ticket"
            />
          </FormGroup>
          <Typography variant="caption" color="textSecondary" className={classes.helper}>
            Útil para fluxos de triagem/escolha de fila logo na abertura do ticket.
          </Typography>

          <Divider style={{ margin: "8px 0" }} />

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={7}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!rules.onInactivity.enabled}
                    onChange={(e) =>
                      setGroup("onInactivity", "enabled", e.target.checked)
                    }
                  />
                }
                label="Por inatividade do contato"
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Minutos sem resposta"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                disabled={!rules.onInactivity.enabled}
                value={rules.onInactivity.minutes}
                onChange={(e) =>
                  setGroup("onInactivity", "minutes", Number(e.target.value))
                }
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="textSecondary" className={classes.helper}>
            Dispara após X minutos sem resposta, nos status selecionados abaixo.
            Para follow-up SDR, use 10080 (7 dias) e marque a opção abaixo.
          </Typography>
          {rules.onInactivity.enabled && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={rules.onInactivity.whenLastMessageFromMe !== false}
                    onChange={(e) =>
                      setGroup(
                        "onInactivity",
                        "whenLastMessageFromMe",
                        e.target.checked
                      )
                    }
                  />
                }
                label="Follow-up: última mensagem foi nossa e o contato não respondeu"
              />
              <FormHelperText>
                Marcado = follow-up (silêncio do contato). Desmarcado = última
                mensagem veio do contato e ficou sem resposta da equipe (ex.:
                suporte após 10 min).
              </FormHelperText>
            </>
          )}

          <Divider style={{ margin: "8px 0" }} />

          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Filtros do ticket
          </Typography>
          <Typography variant="caption" color="textSecondary" className={classes.helper}>
            Define em quais status o fluxo pode ser acionado (pode marcar vários)
            e se dispara mesmo com atendente já definido no ticket.
          </Typography>
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel id="ticket-statuses-label">Status permitidos</InputLabel>
            <Select
              multiple
              labelId="ticket-statuses-label"
              label="Status permitidos"
              value={rules.ticketFilters?.statuses || []}
              onChange={(e) =>
                setGroup("ticketFilters", "statuses", e.target.value)
              }
              renderValue={(selected) =>
                TICKET_STATUS_OPTIONS.filter((o) => selected.includes(o.value))
                  .map((o) => o.label)
                  .join(", ")
              }
            >
              {TICKET_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox
                    color="primary"
                    checked={(rules.ticketFilters?.statuses || []).includes(
                      opt.value
                    )}
                  />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Sem seleção salva, o padrão legado é: inatividade só em pendente;
              novo ticket em qualquer status (exceto fechado).
            </FormHelperText>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!rules.ticketFilters?.allowWithUser}
                onChange={(e) =>
                  setGroup("ticketFilters", "allowWithUser", e.target.checked)
                }
              />
            }
            label="Acionar mesmo com atendente definido no ticket"
          />
          <FormHelperText style={{ marginTop: 0, marginBottom: 8 }}>
            Útil quando a conexão WhatsApp já atribui um usuário padrão (ticket
            nasce aberto). Desmarcado = só tickets sem atendente.
          </FormHelperText>

          <Divider style={{ margin: "8px 0" }} />

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!rules.conditions.enabled}
                  onChange={(e) =>
                    setGroup("conditions", "enabled", e.target.checked)
                  }
                />
              }
              label="Habilitar condições (cenários)"
            />
          </FormGroup>
          <Typography variant="caption" color="textSecondary" className={classes.helper}>
            Cada cenário agrupa condições (tags, kanban, conteúdo). Use OU/E entre
            cenários e dentro de cada grupo.
          </Typography>

          {rules.conditions.enabled && (
            <>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel id="groups-match-label">Entre cenários</InputLabel>
                <Select
                  labelId="groups-match-label"
                  label="Entre cenários"
                  value={rules.conditions.groupsMatch || "any"}
                  onChange={(e) =>
                    setGroup("conditions", "groupsMatch", e.target.value)
                  }
                >
                  <MenuItem value="any">Qualquer cenário (OU)</MenuItem>
                  <MenuItem value="all">Todos os cenários (E)</MenuItem>
                </Select>
              </FormControl>

              {(rules.conditions.groups || []).map((group, groupIndex) => (
                <Paper key={group.id} className={classes.groupCard} elevation={0}>
                  <div className={classes.groupHeader}>
                    <Typography variant="subtitle2">
                      Cenário {groupIndex + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        updateConditions((conds) => ({
                          ...conds,
                          groups: (conds.groups || []).filter(
                            (_, i) => i !== groupIndex
                          ),
                        }))
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>

                  <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel>Dentro do cenário</InputLabel>
                    <Select
                      label="Dentro do cenário"
                      value={group.match || "all"}
                      onChange={(e) =>
                        updateConditions((conds) => {
                          const groups = [...(conds.groups || [])];
                          groups[groupIndex] = {
                            ...groups[groupIndex],
                            match: e.target.value,
                          };
                          return { ...conds, groups };
                        })
                      }
                    >
                      <MenuItem value="all">Todas as condições (E)</MenuItem>
                      <MenuItem value="any">Qualquer condição (OU)</MenuItem>
                    </Select>
                  </FormControl>

                  {(group.conditions || []).map((condition, conditionIndex) => (
                    <div
                      key={`${group.id}-c${conditionIndex}`}
                      className={classes.conditionCard}
                    >
                      <div className={classes.conditionHeader}>
                        <FormControl
                          margin="dense"
                          variant="outlined"
                          style={{ minWidth: 200 }}
                        >
                          <InputLabel>Tipo</InputLabel>
                          <Select
                            label="Tipo"
                            value={condition.type}
                            onChange={(e) =>
                              updateConditions((conds) => {
                                const groups = [...(conds.groups || [])];
                                const g = { ...groups[groupIndex] };
                                const conditions = [...(g.conditions || [])];
                                conditions[conditionIndex] = createEmptyCondition(
                                  e.target.value
                                );
                                g.conditions = conditions;
                                groups[groupIndex] = g;
                                return { ...conds, groups };
                              })
                            }
                          >
                            <MenuItem value="contactTag">Tag do contato</MenuItem>
                            <MenuItem value="kanbanTag">Etapa Kanban</MenuItem>
                            <MenuItem value="messageContains">
                              Conteúdo de mensagem
                            </MenuItem>
                            <MenuItem value="lastMessageAge">
                              Tempo da última mensagem
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateConditions((conds) => {
                              const groups = [...(conds.groups || [])];
                              const g = { ...groups[groupIndex] };
                              g.conditions = (g.conditions || []).filter(
                                (_, i) => i !== conditionIndex
                              );
                              groups[groupIndex] = g;
                              return { ...conds, groups };
                            })
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                      {renderConditionFields(
                        groupIndex,
                        conditionIndex,
                        condition
                      )}
                    </div>
                  ))}

                  <Button
                    size="small"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      updateConditions((conds) => {
                        const groups = [...(conds.groups || [])];
                        const g = { ...groups[groupIndex] };
                        g.conditions = [
                          ...(g.conditions || []),
                          createEmptyCondition("contactTag"),
                        ];
                        groups[groupIndex] = g;
                        return { ...conds, groups };
                      })
                    }
                  >
                    Adicionar condição
                  </Button>
                </Paper>
              ))}

              <Button
                color="primary"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() =>
                  updateConditions((conds) => ({
                    ...conds,
                    groups: [...(conds.groups || []), createEmptyGroup()],
                  }))
                }
              >
                Adicionar cenário
              </Button>
            </>
          )}

          <Divider style={{ margin: "8px 0" }} />

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!rules.ai.enabled}
                  onChange={(e) => setGroup("ai", "enabled", e.target.checked)}
                />
              }
              label="Usar Agente de IA para decidir a ação"
            />
          </FormGroup>
          <Typography variant="caption" color="textSecondary" className={classes.helper}>
            Quando um gatilho acima disparar, a IA analisa a conversa e decide o que
            fazer (entrar em uma etapa do fluxo, encerrar, mudar de fila ou aplicar
            tag). Se desativado, o fluxo inicia do começo.
          </Typography>

          {rules.ai.enabled && (
            <>
              <TextField
                label="API Key"
                type={showApiKey ? "text" : "password"}
                variant="outlined"
                margin="dense"
                fullWidth
                value={rules.ai.apiKey}
                onChange={(e) => setGroup("ai", "apiKey", e.target.value)}
                helperText="Deixe em branco para usar a credencial padrão das Configurações"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel>Modelo</InputLabel>
                    <Select
                      label="Modelo"
                      value={rules.ai.model}
                      onChange={(e) => setGroup("ai", "model", e.target.value)}
                    >
                      {allowedModels.map((model) => (
                        <MenuItem key={model} value={model}>
                          {getModelLabel(model)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Mensagens de contexto"
                    type="number"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={rules.ai.maxMessages}
                    onChange={(e) =>
                      setGroup("ai", "maxMessages", Number(e.target.value))
                    }
                    inputProps={{ min: 1, max: 50 }}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Instruções para a IA (opcional)"
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                rows={3}
                value={rules.ai.prompt}
                onChange={(e) => setGroup("ai", "prompt", e.target.value)}
              />

              <Typography variant="subtitle2" className={classes.sectionTitle}>
                Ações permitidas à IA
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!rules.ai.allowStage}
                      onChange={(e) =>
                        setGroup("ai", "allowStage", e.target.checked)
                      }
                    />
                  }
                  label="Decidir a etapa/nó do fluxo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!rules.ai.allowClose}
                      onChange={(e) =>
                        setGroup("ai", "allowClose", e.target.checked)
                      }
                    />
                  }
                  label="Encerrar o ticket"
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!rules.ai.allowQueue}
                      onChange={(e) =>
                        setGroup("ai", "allowQueue", e.target.checked)
                      }
                    />
                  }
                  label="Mudar a fila do ticket"
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!rules.ai.allowTag}
                      onChange={(e) =>
                        setGroup("ai", "allowTag", e.target.checked)
                      }
                    />
                  }
                  label="Aplicar tag (kanban)"
                />
              </FormGroup>
            </>
          )}

          <Divider style={{ margin: "16px 0 8px" }} />

          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Assinatura nas mensagens
          </Typography>
          <Typography variant="caption" color="textSecondary" className={classes.helper}>
            Quando habilitada, as mensagens de texto deste fluxo são enviadas com
            o prefixo *Nome:* (como no chat). Por padrão não inclui assinatura.
            O nome pode ser qualquer texto, inclusive diferente do usuário.
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!rules.messageSignature?.enabled}
                  onChange={(e) =>
                    setGroup("messageSignature", "enabled", e.target.checked)
                  }
                />
              }
              label="Incluir assinatura ao disparar mensagens"
            />
          </FormGroup>
          {!!rules.messageSignature?.enabled && (
            <TextField
              label="Nome da assinatura"
              variant="outlined"
              margin="dense"
              fullWidth
              value={rules.messageSignature?.name || ""}
              onChange={(e) =>
                setGroup("messageSignature", "name", e.target.value)
              }
              placeholder="Ex.: Atendimento, Suporte, Maria"
              helperText="Aparece como *Nome:* antes do texto da mensagem"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderTriggerRulesModal;
